import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/lib/auth-context";
import { summary, loads as loadsApi, vehicles as vApi, finance } from "@/lib/api";
import { money } from "@/components/LoadMoney";
import { Plus, Scan, Truck, MapPin, ClockCounterClockwise, PackageOpen, ArrowRight, CarProfile } from "@phosphor-icons/react";
import TrainingWelcome from "@/components/TrainingWelcome";
import ReferCard from "@/components/ReferCard";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [loads, setLoads] = useState(null);
  const [recent, setRecent] = useState([]);
  const [month, setMonth] = useState(null);

  useEffect(() => {
    summary().then(setStats);
    finance.month().then(setMonth).catch(() => setMonth(null));
    loadsApi.list({ status: "active" }).then(setLoads);
    vApi.list().then(v => setRecent(v.slice(0, 5)));
  }, []);

  return <AppShell>
    <TrainingWelcome/>
    <div className="mb-6">
      <div className="eyebrow text-accent">Owner-Operator</div>
      <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">
        Hey {user?.name?.split(" ")[0] || "driver"} — <span className="text-muted-foreground font-normal">roll call.</span>
      </h1>
    </div>

    {/* Primary actions */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
      <ActionBtn to="/app/loads/new" icon={Plus} label="New Load" primary testid="action-new-load" />
      <ActionBtn to="/app/loads" icon={Scan} label="Scan / Add Vehicle" testid="action-scan" />
      <ActionBtn to="/app/locations/new" icon={MapPin} label="Save Location" testid="action-location" />
      <ActionBtn to="/app/history" icon={ClockCounterClockwise} label="History" testid="action-history" />
    </div>

    {/* Stats */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-8">
      <Stat label="In transit" value={stats?.in_transit} tid="stat-in-transit"/>
      <Stat label="Awaiting pickup" value={stats?.awaiting_pickup} tid="stat-awaiting"/>
      <Stat label="Delivered" value={stats?.delivered} tid="stat-delivered"/>
      <Stat label="Active loads" value={stats?.active_loads} tid="stat-active-loads"/>
    </div>

    {month && <MonthMoney m={month}/>}

    {(user?.fleet_role || "owner") === "owner" && <ReferCard/>}

    <div className="grid md:grid-cols-2 gap-6">
      <section>
        <SectionHeader title="Active loads" link="/app/loads"/>
        <div className="space-y-2">
          {loads?.length === 0 && <Empty label="No active loads yet. Tap New Load to begin."/>}
          {(loads || []).map(l => (
            <Link to={`/app/loads/${l.id}`} key={l.id} data-testid={`load-card-${l.id}`}
              className="block bg-white border border-border/60 hover:border-accent p-4 slide-up">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono-tech text-xs text-muted-foreground">#{l.load_number}</div>
                  <div className="font-display font-bold text-lg">{l.broker || "No broker"}</div>
                  <div className="text-sm text-muted-foreground">{l.pickup_city || "—"} → {l.delivery_city || "—"}</div>
                </div>
                <div className="text-right">
                  <div className="eyebrow text-accent">{l.vehicle_count ?? 0} veh</div>
                  <ArrowRight size={18}/>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
      <section>
        <SectionHeader title="Recent vehicles" link="/app/history"/>
        <div className="space-y-2">
          {recent.length === 0 && <Empty label="No vehicles recorded yet."/>}
          {recent.map(v => (
            <Link to={`/app/vehicles/${v.id}`} key={v.id} data-testid={`veh-card-${v.id}`}
              className="block bg-white border border-border/60 hover:border-accent p-4">
              <div className="flex items-center gap-3">
                <CarProfile size={28} weight="duotone" className="text-primary"/>
                <div className="flex-1">
                  <div className="font-mono-tech text-[11px] text-muted-foreground">{v.vin}</div>
                  <div className="font-display font-bold">{v.year} {v.make} {v.model}</div>
                  <div className="text-xs text-muted-foreground">Status: {v.status.replace("_"," ")}</div>
                </div>
                <ArrowRight size={18}/>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  </AppShell>;
}

function MonthMoney({ m }) {
  return <section className="mb-8 bg-primary text-primary-foreground p-5 border-l-8 border-accent" data-testid="month-money">
    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
      <h2 className="font-display font-bold text-xl">{m.month} {m.scope === "mine" ? "— your loads" : "— fleet"}</h2>
      <span className="eyebrow opacity-80" data-testid="month-loads">{m.loads} load{m.loads === 1 ? "" : "s"}</span>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
      <MoneyStat k="Revenue" v={money(m.revenue)} tid="month-revenue"/>
      <MoneyStat k="Fuel + tolls" v={money(m.costs)} tid="month-costs"/>
      <MoneyStat k="Profit" v={money(m.profit)} tid="month-profit" big/>
      <MoneyStat k="Loaded miles" v={m.loaded_miles ? m.loaded_miles.toLocaleString() : "—"} tid="month-miles"/>
      <MoneyStat k="Profit / mile" v={m.profit_per_mile == null ? "—" : money(m.profit_per_mile)} tid="month-ppm"/>
    </div>
  </section>;
}
function MoneyStat({ k, v, tid, big }) {
  return <div data-testid={tid}>
    <div className="eyebrow opacity-70">{k}</div>
    <div className={`font-display font-black ${big ? "text-3xl text-accent" : "text-2xl"}`}>{v}</div>
  </div>;
}
function ActionBtn({ to, icon:Icon, label, primary, testid }) {
  return <Link to={to} data-testid={testid}
    className={`relative flex flex-col items-start gap-3 p-5 min-h-[112px] border-2 ${primary ? 'bg-accent border-accent text-accent-foreground' : 'bg-white border-primary text-primary hover:bg-primary hover:text-white'} transition-colors`}>
    <Icon size={28} weight="bold"/>
    <span className="font-display font-black uppercase tracking-wider text-sm leading-tight">{label}</span>
  </Link>;
}
function Stat({ label, value, tid }) {
  return <div className="bg-white border border-border/60 p-4" data-testid={tid}>
    <div className="eyebrow text-muted-foreground">{label}</div>
    <div className="font-display font-black text-3xl text-primary mt-1">{value ?? "—"}</div>
  </div>;
}
function SectionHeader({ title, link }) {
  return <div className="flex items-center justify-between mb-3">
    <h2 className="font-display font-bold text-xl">{title}</h2>
    {link && <Link to={link} className="text-xs eyebrow text-accent">View all →</Link>}
  </div>;
}
function Empty({ label }) {
  return <div className="border-2 border-dashed border-border p-6 text-sm text-muted-foreground text-center">{label}</div>;
}
